/**
 * Utility functions for Constituent
 */

export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(num)
}

export function formatExactNumber(num: number): string {
  if (!Number.isFinite(num)) return '—'
  return new Intl.NumberFormat('en-US').format(num)
}

export function getPartyColor(party: string): { bg: string; text: string } {
  const p = party.toUpperCase()
  switch (p) {
    case 'D':
      return { bg: 'bg-blue-100', text: 'text-blue-800' }
    case 'R':
      return { bg: 'bg-red-100', text: 'text-red-800' }
    case 'I':
    case 'IND':
    case 'INDEPENDENT':
      return { bg: 'bg-gray-100', text: 'text-gray-800' }
    case 'L':
    case 'LIBERTARIAN':
      return { bg: 'bg-yellow-100', text: 'text-yellow-800' }
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-800' }
  }
}

export function getOfficeName(office: string): string {
  switch (office.toUpperCase()) {
    case 'P':
      return 'President'
    case 'S':
      return 'Senate'
    case 'H':
      return 'House'
    default:
      return office
  }
}

/** Convert a title to title case, handling ALL CAPS and lowercase input. */
export function toTitleCase(text: string): string {
  const s = String(text || '').trim()
  if (!s) return s
  const SMALL_WORDS = new Set([
    'a','an','and','as','at','but','by','for','if','in','nor','of','on','or','so','the','to','up','yet','vs',
  ])
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((word, i) => {
      if (i === 0 || !SMALL_WORDS.has(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1)
      }
      return word
    })
    .join(' ')
}

export function formatOfficialTitle(text: string): string {
  return String(text || '').trim().replace(/\s+/g, ' ')
}

/**
 * Format a state bill title uniformly: "State TYPE Number: Title Case Title"
 * Returns null if the provider_id doesn't match a known state pattern.
 */
export function formatStateBillTitle(providerId: string, title: string): string | null {
  const pid = String(providerId || '').trim()
  const raw = String(title || '').trim()

  // ILGA: ilga:GA:TYPE:NUMBER
  const ilga = pid.match(/^ilga:\d+:([A-Z]{2}):(\d+)$/i)
  if (ilga) {
    const label = `Illinois ${ilga[1].toUpperCase()} ${parseInt(ilga[2], 10)}`
    return raw ? `${label}: ${formatOfficialTitle(raw)}` : label
  }

  // Wisconsin: wisconsin:YEAR:TYPE:NUMBER
  const wi = pid.match(/^wisconsin:\d+:([A-Z]+):(\d+)$/i)
  if (wi) {
    const label = `Wisconsin ${wi[1].toUpperCase()} ${parseInt(wi[2], 10)}`
    return raw ? `${label}: ${formatOfficialTitle(raw)}` : label
  }

  return null
}

export function formatDate(dateString: string): string {
  // A bare calendar date ("YYYY-MM-DD") carries no time or zone — it should
  // render as that exact day everywhere. `new Date("2024-03-15")` parses it as
  // UTC midnight, which Intl then formats in the local zone, shifting it back a
  // day for any viewer behind UTC (e.g. "Mar 14" in US timezones). Build the
  // date from local parts instead so the calendar day is preserved. Full ISO
  // datetimes (with a time/zone) keep their normal local-conversion behavior.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString)
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(dateString)
  if (!isFinite(date.getTime())) return dateString
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}
